import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  XMarkIcon,
  PhotoIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  LinkIcon,
} from "@heroicons/react/24/outline";
import { createMarketingPost, updateMarketingPost, previewAudience } from "../../api/marketingPostsAPI";
import { uploadChatFile } from "../../api/operator";
import { getRegions, getDistricts } from "../../api/locations";

export const POST_TYPES = [
  { value: "promo", label: "Акция", cls: "bg-violet-500/15 text-violet-500 border-violet-500/30" },
  { value: "news", label: "Новости", cls: "bg-sky-500/15 text-sky-500 border-sky-500/30" },
  { value: "system", label: "Системное", cls: "bg-gray-500/15 text-text-secondary border-gray-500/30" },
];

const USER_TYPE_LABELS = {
  individual: "Физ. лица",
  business: "Бизнес",
  support: "Поддержка",
  admin: "Админы",
  moderator: "Модераторы",
  operator: "Операторы",
  sales_manager: "Менеджеры продаж",
};
const LANG_LABELS = { kz: "Қазақша", ru: "Русский", en: "English", ch: "中文" };
const MAX_IMAGE_MB = 10;

const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

export const EMPTY_FORM = {
  type: "promo",
  title: "",
  body: "",
  image_url: "",
  video_url: "",
  cta_link: "",
  display_modes: ["mobile_story", "web_banner"],
  banner_active: false,
  broadcast_to_chat: false,
  audience_user_types: [],
  audience_region_ids: [],
  audience_district_ids: [],
  audience_languages: [],
  audience_active_only: true,
  publish_at: "",
  expires_at: "",
};

export const formFromPost = (full) => ({
  type: full.type || "promo",
  title: full.title || "",
  body: full.body || "",
  image_url: full.image_url || "",
  video_url: full.video_url || "",
  cta_link: full.cta_link || "",
  display_modes: full.display_modes || [],
  banner_active: !!full.banner_active,
  broadcast_to_chat: !!full.broadcast_to_chat,
  audience_user_types: full.audience?.user_types || [],
  audience_region_ids: full.audience?.region_ids || [],
  audience_district_ids: full.audience?.district_ids || [],
  audience_languages: full.audience?.languages || [],
  audience_active_only: full.audience?.active_only !== false,
  publish_at: full.publish_at ? full.publish_at.slice(0, 16) : "",
  expires_at: full.expires_at ? full.expires_at.slice(0, 16) : "",
});

const nullIfEmpty = (arr) => (arr.length ? arr : null);

const audiencePayload = (form) => ({
  audience_user_types: nullIfEmpty(form.audience_user_types),
  audience_region_ids: nullIfEmpty(form.audience_region_ids),
  audience_district_ids: nullIfEmpty(form.audience_district_ids),
  audience_languages: nullIfEmpty(form.audience_languages),
  audience_active_only: form.audience_active_only,
});

const buildPayload = (form) => ({
  type: form.type,
  title: form.title.trim(),
  body: form.body.trim() || null,
  image_url: form.image_url.trim() || null,
  video_url: form.video_url.trim() || null,
  cta_link: form.cta_link.trim() || null,
  display_modes: form.display_modes,
  banner_active: form.display_modes.includes("web_banner") && form.banner_active,
  broadcast_to_chat: form.broadcast_to_chat,
  publish_at: form.publish_at || null,
  expires_at: form.expires_at || null,
  ...audiencePayload(form),
});

const Section = ({ title, hint, children }) => (
  <section className="space-y-3 border-t border-border-primary pt-5 first:border-0 first:pt-0">
    <div>
      <h4 className="text-sm font-semibold text-text-primary">{title}</h4>
      {hint && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
    {children}
  </section>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
      active ? "border-[#4F45E5]/50 bg-[#4F45E5]/15 text-text-primary" : "border-border-primary text-text-secondary hover:bg-bg-tertiary"
    }`}
  >
    {children}
  </button>
);

const ToggleCard = ({ checked, onChange, icon: Icon, title, hint, children }) => (
  <div className={`rounded-lg border p-3 transition-colors ${checked ? "border-[#4F45E5] bg-[#4F45E5]/[0.07]" : "border-border-primary"}`}>
    <label className="flex cursor-pointer items-start gap-3">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-[#4F45E5]" />
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${checked ? "text-[#7C3AED]" : "text-text-muted"}`} />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text-primary">{title}</span>
        {hint && <span className="block text-xs text-text-secondary">{hint}</span>}
      </span>
    </label>
    {checked && children && <div className="mt-2 pl-7">{children}</div>}
  </div>
);

const PostEditor = ({ editing, initialForm, audienceOptions, onClose, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [regions, setRegions] = useState([]);
  const [districtsByRegion, setDistrictsByRegion] = useState({});
  const [regionQuery, setRegionQuery] = useState("");
  const [estimated, setEstimated] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmBroadcast, setConfirmBroadcast] = useState(false);
  const [previewMode, setPreviewMode] = useState("story");
  const fileInputRef = useRef(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (key, value) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value] }));

  useEffect(() => {
    getRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  // Районы подгружаем для выбранных областей
  useEffect(() => {
    form.audience_region_ids.forEach((rid) => {
      if (districtsByRegion[rid]) return;
      getDistricts(rid)
        .then((list) => setDistrictsByRegion((m) => ({ ...m, [rid]: list })))
        .catch(() => setDistrictsByRegion((m) => ({ ...m, [rid]: [] })));
    });
  }, [form.audience_region_ids, districtsByRegion]);

  // Охват пересчитывается автоматически при изменении аудитории
  const audienceKey = JSON.stringify(audiencePayload(form));
  useEffect(() => {
    let cancelled = false;
    setEstimating(true);
    const t = setTimeout(async () => {
      try {
        const data = await previewAudience(JSON.parse(audienceKey));
        if (!cancelled) setEstimated(data.estimated_recipients_count);
      } catch {
        if (!cancelled) setEstimated(null);
      } finally {
        if (!cancelled) setEstimating(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [audienceKey]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const regionName = (id) => regions.find((r) => r.id === id)?.name_ru || `#${id}`;
  const filteredRegions = useMemo(() => {
    const q = regionQuery.trim().toLowerCase();
    return q ? regions.filter((r) => `${r.name_ru} ${r.name_kz}`.toLowerCase().includes(q)) : regions;
  }, [regions, regionQuery]);

  const removeRegion = (rid) =>
    setForm((f) => ({
      ...f,
      audience_region_ids: f.audience_region_ids.filter((x) => x !== rid),
      audience_district_ids: f.audience_district_ids.filter((d) => !(districtsByRegion[rid] || []).some((x) => x.id === d)),
    }));

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return setError("Можно загрузить только изображение.");
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) return setError(`Изображение больше ${MAX_IMAGE_MB} МБ.`);
    setError(null);
    setUploading(true);
    try {
      set("image_url", await uploadChatFile(file));
    } catch (err) {
      setError(err.message || "Ошибка загрузки изображения");
    } finally {
      setUploading(false);
    }
  };

  const validate = () => {
    if (!form.title.trim()) return "Укажите заголовок.";
    if (form.display_modes.includes("mobile_story") && !form.image_url.trim()) return "Для сториз нужно изображение.";
    if (form.publish_at && form.expires_at && form.expires_at <= form.publish_at) return "Дата окончания должна быть позже даты публикации.";
    if (form.cta_link && !/^https?:\/\//.test(form.cta_link.trim())) return "Ссылка должна начинаться с http:// или https://";
    return null;
  };

  // Рассылка уходит сразу при создании — подтверждаем, если её ещё не было
  const needsBroadcastConfirm = form.broadcast_to_chat && !editing?.broadcast_sent_at;

  const submit = async (e) => {
    e?.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    if (needsBroadcastConfirm && !confirmBroadcast) return setConfirmBroadcast(true);
    setSaving(true);
    setError(null);
    try {
      const payload = buildPayload(form);
      const saved = editing ? await updateMarketingPost(editing.id, payload) : await createMarketingPost(payload);
      onSaved(saved, editing ? `Пост #${editing.id} сохранён` : `Пост #${saved?.id ?? ""} создан`);
    } catch (err) {
      setError(err.message);
      setConfirmBroadcast(false);
      setSaving(false);
    }
  };

  const typeMeta = POST_TYPES.find((t) => t.value === form.type);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <form onSubmit={submit} role="dialog" aria-modal="true" className="flex h-full w-full max-w-5xl flex-col bg-bg-card text-left shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-primary px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">{editing ? `Пост #${editing.id}` : "Новый маркетинг-пост"}</h3>
            <p className="text-xs text-text-secondary">Сториз в приложении · баннер на сайте · рассылка в чаты</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Закрыть (Esc)" className="rounded-lg p-1.5 text-text-muted hover:bg-bg-tertiary hover:text-text-primary">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20rem]">
          {/* Form */}
          <div className="space-y-5 overflow-y-auto px-6 py-5">
            <Section title="Контент">
              <div className="flex gap-2">
                {POST_TYPES.map((t) => (
                  <Chip key={t.value} active={form.type === t.value} onClick={() => set("type", t.value)}>
                    {t.label}
                  </Chip>
                ))}
              </div>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Заголовок *</span>
                <input autoFocus value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Например: Новогодняя акция −30%" className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-text-secondary">Текст</span>
                <textarea rows={3} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="Описание (необязательно)" className={`${inputCls} resize-y`} />
              </label>

              <div>
                <span className="mb-1 block text-xs font-medium text-text-secondary">
                  Изображение {form.display_modes.includes("mobile_story") && <span className="text-text-muted">— обязательно для сториз</span>}
                </span>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                <div className="flex gap-2">
                  <input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://… или загрузите файл" className={`${inputCls} flex-1`} />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border-primary px-3 text-sm font-medium text-text-secondary hover:bg-bg-tertiary disabled:opacity-50"
                  >
                    {uploading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PhotoIcon className="h-4 w-4" />}
                    Загрузить
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">Видео (URL)</span>
                  <input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://…" className={inputCls} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">Ссылка по клику</span>
                  <input value={form.cta_link} onChange={(e) => set("cta_link", e.target.value)} placeholder="https://agroland.kz/…" className={inputCls} />
                </label>
              </div>
            </Section>

            <Section title="Где показывать">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <ToggleCard
                  checked={form.display_modes.includes("mobile_story")}
                  onChange={() => toggle("display_modes", "mobile_story")}
                  icon={DevicePhoneMobileIcon}
                  title="Сториз в приложении"
                  hint="Лента историй на главной"
                />
                <ToggleCard
                  checked={form.display_modes.includes("web_banner")}
                  onChange={() => toggle("display_modes", "web_banner")}
                  icon={ComputerDesktopIcon}
                  title="Баннер на сайте"
                  hint="Главная страница agroland.kz"
                >
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
                    <input type="checkbox" checked={form.banner_active} onChange={(e) => set("banner_active", e.target.checked)} className="h-3.5 w-3.5 accent-[#4F45E5]" />
                    Сделать текущим баннером (заменит предыдущий)
                  </label>
                </ToggleCard>
              </div>
            </Section>

            <Section title="Аудитория" hint="Кому показывать и кому отправлять рассылку. Ничего не выбрано — все.">
              <div>
                <span className="mb-1.5 block text-xs font-medium text-text-secondary">Типы пользователей</span>
                <div className="flex flex-wrap gap-1.5">
                  {(audienceOptions.user_types || []).map((t) => (
                    <Chip key={t} active={form.audience_user_types.includes(t)} onClick={() => toggle("audience_user_types", t)}>
                      {USER_TYPE_LABELS[t] || t}
                    </Chip>
                  ))}
                </div>
              </div>
              <div>
                <span className="mb-1.5 block text-xs font-medium text-text-secondary">Язык приложения</span>
                <div className="flex flex-wrap gap-1.5">
                  {(audienceOptions.languages?.length ? audienceOptions.languages : ["kz", "ru", "en"]).map((l) => (
                    <Chip key={l} active={form.audience_languages.includes(l)} onClick={() => toggle("audience_languages", l)}>
                      {LANG_LABELS[l] || l.toUpperCase()}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <span className="mb-1.5 block text-xs font-medium text-text-secondary">Регионы</span>
                {form.audience_region_ids.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {form.audience_region_ids.map((rid) => (
                      <span key={rid} className="inline-flex items-center gap-1 rounded-full border border-[#4F45E5]/40 bg-[#4F45E5]/10 py-0.5 pl-3 pr-1 text-xs text-text-primary">
                        {regionName(rid)}
                        <button type="button" onClick={() => removeRegion(rid)} aria-label="Убрать регион" className="rounded-full p-0.5 hover:bg-[#4F45E5]/20">
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="rounded-lg border border-border-primary">
                  <div className="relative border-b border-border-primary">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
                    <input
                      value={regionQuery}
                      onChange={(e) => setRegionQuery(e.target.value)}
                      placeholder="Найти область…"
                      className="w-full rounded-t-lg bg-transparent py-2 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto p-1">
                    {filteredRegions.map((r) => (
                      <label key={r.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-text-primary hover:bg-bg-tertiary">
                        <input
                          type="checkbox"
                          checked={form.audience_region_ids.includes(r.id)}
                          onChange={() => (form.audience_region_ids.includes(r.id) ? removeRegion(r.id) : toggle("audience_region_ids", r.id))}
                          className="h-3.5 w-3.5 accent-[#4F45E5]"
                        />
                        {r.name_ru}
                      </label>
                    ))}
                    {filteredRegions.length === 0 && <p className="px-2 py-3 text-center text-xs text-text-muted">{regions.length ? "Ничего не найдено" : "Загрузка…"}</p>}
                  </div>
                </div>
              </div>

              {form.audience_region_ids.length > 0 && (
                <div>
                  <span className="mb-1.5 block text-xs font-medium text-text-secondary">Районы — необязательно, сужают выбранные области</span>
                  <div className="space-y-2">
                    {form.audience_region_ids.map((rid) => (
                      <div key={rid}>
                        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{regionName(rid)}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(districtsByRegion[rid] || []).map((d) => (
                            <Chip key={d.id} active={form.audience_district_ids.includes(d.id)} onClick={() => toggle("audience_district_ids", d.id)}>
                              {d.name_ru}
                            </Chip>
                          ))}
                          {!districtsByRegion[rid] && <span className="text-xs text-text-muted">Загрузка…</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <label className="flex cursor-pointer items-center gap-2 text-sm text-text-primary">
                <input type="checkbox" checked={form.audience_active_only} onChange={(e) => set("audience_active_only", e.target.checked)} className="h-4 w-4 accent-[#4F45E5]" />
                Только активные аккаунты
              </label>

              <div className="flex items-center gap-2 rounded-lg bg-bg-primary px-3 py-2 text-sm">
                <UsersIcon className="h-4 w-4 text-text-muted" />
                <span className="text-text-secondary">Охват:</span>
                {estimating ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-text-muted" />
                ) : (
                  <b className="tabular-nums text-text-primary">{estimated === null ? "—" : `≈ ${Number(estimated).toLocaleString("ru-RU")}`}</b>
                )}
                <span className="text-text-muted">получателей</span>
              </div>
            </Section>

            <Section title="Рассылка и период">
              <ToggleCard
                checked={form.broadcast_to_chat}
                onChange={(v) => {
                  set("broadcast_to_chat", v);
                  setConfirmBroadcast(false);
                }}
                icon={ChatBubbleLeftRightIcon}
                title="Отправить сообщение в чат"
                hint={
                  editing?.broadcast_sent_at
                    ? `Уже отправлено ${editing.broadcast_recipients_count ?? "?"} получателям. Повторно — «Переотправить» в списке.`
                    : "Системное сообщение всем из аудитории"
                }
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">Опубликовать с</span>
                  <input type="datetime-local" value={form.publish_at} onChange={(e) => set("publish_at", e.target.value)} className={inputCls} />
                  <span className="mt-0.5 block text-[11px] text-text-muted">Пусто — сразу</span>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-text-secondary">Скрыть после</span>
                  <input type="datetime-local" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} className={inputCls} />
                  <span className="mt-0.5 block text-[11px] text-text-muted">Пусто — бессрочно</span>
                </label>
              </div>
            </Section>
          </div>

          {/* Preview */}
          <aside className="hidden overflow-y-auto border-l border-border-primary bg-bg-primary/50 p-5 lg:block">
            <div className="mb-3 flex rounded-lg border border-border-primary bg-bg-card p-0.5">
              {[
                ["story", "Сториз"],
                ["banner", "Баннер"],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setPreviewMode(k)}
                  className={`flex-1 rounded-md py-1 text-xs font-semibold ${previewMode === k ? "bg-accent-grad text-white" : "text-text-secondary"}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {previewMode === "story" ? (
              <div className="relative mx-auto aspect-[9/16] w-56 overflow-hidden rounded-2xl bg-gray-800 shadow-lg">
                {form.image_url ? (
                  <img src={form.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => (e.currentTarget.style.opacity = 0)} />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40">
                    <PhotoIcon className="h-10 w-10" />
                  </div>
                )}
                <div className="absolute inset-x-2 top-2 h-0.5 rounded bg-white/70" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-10 text-white">
                  <span className={`mb-1 inline-block rounded border px-1.5 text-[10px] ${typeMeta.cls}`}>{typeMeta.label}</span>
                  <p className="text-sm font-semibold leading-tight">{form.title || "Заголовок"}</p>
                  {form.body && <p className="mt-1 line-clamp-3 text-[11px] text-white/80">{form.body}</p>}
                  {form.cta_link && (
                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-900">
                      <LinkIcon className="h-3 w-3" /> Подробнее
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl bg-gray-800 shadow-lg">
                <div className="relative aspect-[3/1]">
                  {form.image_url ? (
                    <img src={form.image_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => (e.currentTarget.style.opacity = 0)} />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/40">
                      <PhotoIcon className="h-8 w-8" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex flex-col justify-center bg-gradient-to-r from-black/70 to-transparent p-4 text-white">
                    <p className="max-w-[70%] text-sm font-semibold leading-tight">{form.title || "Заголовок"}</p>
                  </div>
                </div>
              </div>
            )}
            <ul className="mt-4 space-y-1.5 text-xs text-text-secondary">
              <li>• {form.display_modes.length ? form.display_modes.map((m) => (m === "mobile_story" ? "сториз" : "баннер")).join(" + ") : "нигде не показывается"}</li>
              <li>• {form.broadcast_to_chat ? `рассылка ≈ ${estimated ?? "?"} пользователям` : "без рассылки"}</li>
              <li>• {form.publish_at ? `с ${new Date(form.publish_at).toLocaleString("ru-RU")}` : "публикация сразу"}</li>
            </ul>
          </aside>
        </div>

        {/* Footer */}
        <div className="border-t border-border-primary px-6 py-4">
          {error && (
            <div role="alert" className="mb-3 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
              {error}
            </div>
          )}
          {confirmBroadcast && (
            <p className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-text-primary">
              {form.publish_at && new Date(form.publish_at) > new Date()
                ? `Сообщение уйдёт в чаты в момент публикации (${new Date(form.publish_at).toLocaleString("ru-RU")}) — `
                : editing
                ? "Сообщение уйдёт в чаты в течение 5 минут — "
                : "Сообщение сразу уйдёт в чаты — "}
              <b>≈ {estimated ?? "?"}</b> пользователям. Отменить рассылку нельзя.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving} className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary">
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                confirmBroadcast ? "bg-amber-500 hover:bg-amber-600" : "bg-accent-grad hover:opacity-90"
              }`}
            >
              {saving && <ArrowPathIcon className="h-4 w-4 animate-spin" />}
              {confirmBroadcast ? "Да, сохранить и разослать" : editing ? "Сохранить" : "Создать пост"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PostEditor;
